import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUS_COLORS = {
  rascunho: "bg-secondary",
  publicado: "bg-[#00A859] text-white",
  arquivado: "bg-muted text-muted-foreground",
};

export default function Comunicados() {
  const { user } = useAuth();
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState(user?.empresa_id || "");
  const [lista, setLista] = useState([]);
  const [novo, setNovo] = useState(false);
  const [form, setForm] = useState({ assunto: "", texto_original: "", tipo: "informativo",
    destinatarios_role: "montador", exige_aceite: false });
  const [salvando, setSalvando] = useState(false);
  const [detalhe, setDetalhe] = useState(null);

  useEffect(() => {
    if (user?.role === "admin") api.get("/empresas").then(r => setEmpresas(r.data));
  }, [user]);

  const load = async () => {
    if (!empresaId) return;
    const r = await api.get(`/empresas/${empresaId}/comunicados`);
    setLista(r.data);
  };

  useEffect(() => { load(); }, [empresaId]);

  const salvarRascunho = async () => {
    if (!form.assunto.trim() || !form.texto_original.trim()) { toast.error("Preencha assunto e texto"); return; }
    setSalvando(true);
    try {
      const r = await api.post(`/empresas/${empresaId}/comunicados`, form);
      toast.success(r.data.ia_disponivel ? "Rascunho criado · IA revisou o texto" : "Rascunho criado");
      setNovo(false); setForm({ assunto: "", texto_original: "", tipo: "informativo",
        destinatarios_role: "montador", exige_aceite: false });
      load();
      setDetalhe(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSalvando(false); }
  };

  const publicar = async (cid) => {
    if (!window.confirm("Publicar comunicado? Todos os destinatários ativos serão notificados.")) return;
    try {
      const r = await api.post(`/empresas/${empresaId}/comunicados/${cid}/publicar`);
      toast.success(`Publicado · ${r.data.destinatarios} destinatário(s) notificado(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-cap">Comunicação · Compliance</div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">Comunicados Formais</h1>
          <p className="text-sm text-muted-foreground mt-2">
            IA revisa cada comunicado para evitar linguagem trabalhista (CLT). Todas as leituras e aceites são auditados juridicamente.
          </p>
        </div>
        <Button onClick={() => setNovo(true)} disabled={!empresaId} className="btn-sharp brand-bg text-white"
          data-testid="com-novo">+ Novo comunicado</Button>
      </div>

      {user?.role === "admin" && (
        <div className="mb-6">
          <Label className="label-cap">Empresa</Label>
          <Select value={empresaId} onValueChange={setEmpresaId}>
            <SelectTrigger className="mt-1 max-w-md" data-testid="com-empresa"><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>{empresas.map(e => <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}

      <div className="border border-border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F4F5] border-b border-border">
            <tr>
              <th className="text-left p-3 label-cap font-medium">Assunto</th>
              <th className="text-left p-3 label-cap font-medium">Tipo</th>
              <th className="text-left p-3 label-cap font-medium">Destinatários</th>
              <th className="text-left p-3 label-cap font-medium">Status</th>
              <th className="text-left p-3 label-cap font-medium">Leituras</th>
              <th className="text-left p-3 label-cap font-medium">Criado em</th>
              <th className="text-left p-3 label-cap font-medium">Ações</th>
            </tr>
          </thead>
          <tbody>
            {lista.length === 0 && <tr><td colSpan={7} className="p-12 text-center text-sm text-muted-foreground">Nenhum comunicado</td></tr>}
            {lista.map(c => (
              <tr key={c.id} className="border-b border-border last:border-0" data-testid={`com-${c.id}`}>
                <td className="p-3 font-medium">{c.assunto}</td>
                <td className="p-3 text-xs uppercase">{c.tipo}</td>
                <td className="p-3 text-xs">{c.destinatarios_role}</td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 text-[10px] uppercase ${STATUS_COLORS[c.status]}`}>{c.status}</span>
                </td>
                <td className="p-3 font-mono text-xs">{(c.leituras || []).length}</td>
                <td className="p-3 text-xs">{new Date(c.criado_em).toLocaleDateString("pt-BR")}</td>
                <td className="p-3">
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" className="btn-sharp h-7 text-[10px]"
                      onClick={() => setDetalhe(c)} data-testid={`com-ver-${c.id}`}>Ver</Button>
                    {c.status === "rascunho" && (
                      <Button size="sm" className="btn-sharp h-7 text-[10px] bg-[#00A859] text-white"
                        onClick={() => publicar(c.id)} data-testid={`com-publicar-${c.id}`}>Publicar</Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dialog NOVO */}
      <Dialog open={novo} onOpenChange={setNovo}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Novo comunicado</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-cap">Assunto *</Label>
              <Input value={form.assunto} onChange={(e) => setForm({ ...form, assunto: e.target.value })}
                data-testid="com-assunto" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-cap">Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger className="mt-1" data-testid="com-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="informativo">Informativo</SelectItem>
                    <SelectItem value="tabela_valores">Mudança de tabela de valores</SelectItem>
                    <SelectItem value="aditivo">Aditivo contratual</SelectItem>
                    <SelectItem value="calendario">Mudança de calendário</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-cap">Destinatários</Label>
                <Select value={form.destinatarios_role} onValueChange={(v) => setForm({ ...form, destinatarios_role: v })}>
                  <SelectTrigger className="mt-1" data-testid="com-dest"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="montador">Apenas Montadores</SelectItem>
                    <SelectItem value="montadora">Apenas Montadoras</SelectItem>
                    <SelectItem value="ambos">Ambos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="label-cap">Texto (IA irá revisar para evitar linguagem trabalhista)</Label>
              <Textarea rows={6} value={form.texto_original}
                onChange={(e) => setForm({ ...form, texto_original: e.target.value })} data-testid="com-texto" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.exige_aceite}
                onChange={(e) => setForm({ ...form, exige_aceite: e.target.checked })}
                data-testid="com-aceite" />
              Exige aceite explícito (registra em log jurídico)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNovo(false)}>Cancelar</Button>
            <Button onClick={salvarRascunho} disabled={salvando} className="brand-bg text-white"
              data-testid="com-salvar">{salvando ? "IA revisando…" : "Criar rascunho"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog DETALHE */}
      <Dialog open={!!detalhe} onOpenChange={(o) => !o && setDetalhe(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{detalhe?.assunto}</DialogTitle></DialogHeader>
          {detalhe && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div><div className="label-cap">Tipo</div><div className="mt-1">{detalhe.tipo}</div></div>
                <div><div className="label-cap">Status</div><div className="mt-1">{detalhe.status}</div></div>
                <div><div className="label-cap">Destinatários</div><div className="mt-1">{detalhe.destinatarios_role}</div></div>
              </div>
              {detalhe.texto_original !== detalhe.texto_revisado && (
                <div>
                  <div className="label-cap">Texto original</div>
                  <div className="border border-border p-3 text-xs bg-[#F4F4F5] whitespace-pre-wrap mt-1">{detalhe.texto_original}</div>
                </div>
              )}
              <div>
                <div className="label-cap text-green-700">{detalhe.ia_disponivel ? "Texto revisado pela IA" : "Texto"}</div>
                <div className="border-l-4 border-[#00A859] p-3 text-sm bg-green-50 whitespace-pre-wrap mt-1">{detalhe.texto_revisado}</div>
              </div>
              {detalhe.ajustes_ia?.length > 0 && (
                <div>
                  <div className="label-cap">Ajustes feitos pela IA</div>
                  <ul className="list-disc pl-5 text-xs text-muted-foreground mt-1">
                    {detalhe.ajustes_ia.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              )}
              {detalhe.leituras?.length > 0 && (
                <div>
                  <div className="label-cap">Leituras ({detalhe.leituras.length})</div>
                  <table className="w-full text-xs mt-1">
                    <tbody>
                      {detalhe.leituras.map((l, i) => (
                        <tr key={i} className="border-b border-border">
                          <td className="p-1">{l.user_email}</td>
                          <td className="p-1 text-muted-foreground">{new Date(l.lido_em).toLocaleString("pt-BR")}</td>
                          <td className="p-1 text-muted-foreground">{l.ip}</td>
                          <td className="p-1">{l.aceite && <span className="text-[#00A859]">✓ aceito</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
